import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw2204f1Api, F2140, DetailRequest } from '../../../services/pw2204f1Api';

export type Mode = 'browse' | 'add' | 'edit';

const emptyDetail = (): DetailRequest => ({
  f2141_11: '', f2141_11a: '', f2141_12: '', f2141_13: 0, f2141_14: 0,
});

export function useSalesReturnForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F2140[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Header fields
  const [warehouse, setWarehouse] = useState('');
  const [docDate, setDocDate] = useState('');
  const [personCode, setPersonCode] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [description, setDescription] = useState('');
  const [taxType, setTaxType] = useState(1);
  const [amountReceived, setAmountReceived] = useState(0);
  const [transportFee, setTransportFee] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);
  const [remark, setRemark] = useState('');
  const [jumpType, setJumpType] = useState('');
  const [details, setDetails] = useState<DetailRequest[]>([emptyDetail()]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await pw2204f1Api.list(1, 1000, keyword);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFromCurrent = useCallback((rec: F2140) => {
    setWarehouse(rec.f2140_07 || '');
    setDocDate(rec.f2140_11 ? rec.f2140_11.substring(0, 10) : '');
    setPersonCode(rec.f2140_12 || '');
    setCustomerCode(rec.f2140_13 || '');
    setInvoiceNo(rec.f2140_14 || '');
    setDescription(rec.f2140_15 || '');
    setTaxType(rec.f2140_18 || 1);
    setAmountReceived(rec.f2140_21 || 0);
    setTransportFee(rec.f2140_23 || 0);
    setShippingFee(rec.f2140_25 || 0);
    setRemark(rec.f2140_30 || '');
    setJumpType(rec.f2140_31 || '');
    setDetails(
      rec.details && rec.details.length > 0
        ? rec.details.map(d => ({
            f2141_04: d.f2141_04 || undefined,
            f2141_11: d.f2141_11 || '',
            f2141_11a: d.f2141_11a || '',
            f2141_12: d.f2141_12 || '',
            f2141_13: d.f2141_13 || 0,
            f2141_14: d.f2141_14 || 0,
            f2141_16: d.f2141_16 || 0,
            f2141_18: d.f2141_18 || '',
            f2141_21: d.f2141_21 || '',
            f2141_22: d.f2141_22 || 0,
          }))
        : [emptyDetail()]
    );
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      // Load full record with details
      pw2204f1Api.get(current.f2140_01).then(full => syncFromCurrent(full)).catch(() => syncFromCurrent(current));
    }
  }, [current, mode, syncFromCurrent]);

  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setWarehouse(''); setDocDate(''); setPersonCode(''); setCustomerCode('');
    setInvoiceNo(''); setDescription(''); setTaxType(1);
    setAmountReceived(0); setTransportFee(0); setShippingFee(0);
    setRemark(''); setJumpType('');
    setDetails([emptyDetail(), emptyDetail(), emptyDetail()]);
  };

  const startEdit = () => {
    if (!current) return;
    if (current.f2140_80 === 'P') {
      message.warning('已核准單據不可修改');
      return;
    }
    setMode('edit');
  };

  const cancel = () => {
    setMode('browse');
    if (current) {
      pw2204f1Api.get(current.f2140_01).then(full => syncFromCurrent(full)).catch(() => syncFromCurrent(current));
    }
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await pw2204f1Api.create({
          f2140_07: warehouse, f2140_11: docDate, f2140_12: personCode,
          f2140_13: customerCode, f2140_14: invoiceNo, f2140_15: description,
          f2140_18: taxType, f2140_21: amountReceived, f2140_23: transportFee,
          f2140_25: shippingFee, f2140_30: remark, f2140_31: jumpType,
          details,
        });
        message.success('新增成功');
      } else {
        await pw2204f1Api.update(current!.f2140_01, {
          f2140_07: warehouse, f2140_11: docDate, f2140_12: personCode,
          f2140_13: customerCode, f2140_14: invoiceNo, f2140_15: description,
          f2140_18: taxType, f2140_21: amountReceived, f2140_23: transportFee,
          f2140_25: shippingFee, f2140_30: remark,
          details,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      if (data.length > 0) {
        const idx = mode === 'add' ? data.length - 1 : Math.min(currentIdx, data.length - 1);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await pw2204f1Api.delete(current.f2140_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || '刪除失敗');
    }
  };

  const addDetailRow = () => setDetails([...details, emptyDetail()]);
  const removeDetailRow = (idx: number) => {
    if (details.length <= 1) return;
    setDetails(details.filter((_, i) => i !== idx));
  };
  const updateDetail = (idx: number, field: string, value: any) => {
    const updated = [...details];
    (updated[idx] as any)[field] = value;
    setDetails(updated);
  };

  const quickSearch = async (keyword: string) => {
    if (isEditing) return;
    const data = await loadRecords(keyword);
    if (data.length > 0) setCurrentIdx(0);
    else setCurrentIdx(-1);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    warehouse, setWarehouse, docDate, setDocDate,
    personCode, setPersonCode, customerCode, setCustomerCode,
    invoiceNo, setInvoiceNo, description, setDescription,
    taxType, setTaxType, amountReceived, setAmountReceived,
    transportFee, setTransportFee, shippingFee, setShippingFee,
    remark, setRemark, jumpType, setJumpType,
    details, setDetails, addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  };
}
